import React, { useState, useMemo } from 'react';
import { Download, FileArchive, Loader2, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useData } from '../App';
import { Button, Card, Input, Select } from '../components/UI';
import { ALL_ORGS, Counterparty, CounterpartyType, Transaction, TransactionStatus, TransactionType } from '../types';
import { accountLabel, formatMoney, orgDisplayName } from '../utils/format';
import { api } from '../services/api';

// Группы операций для фильтра и колонки «Тип»
type OpGroup = 'INCOME' | 'EXPENSE' | 'SALARY' | 'UNOFFICIAL_SALARY' | 'SELF_EMPLOYED' | 'TAX' | 'OWNER_WITHDRAWAL' | 'DIVIDENDS' | 'NON_TAX' | 'INTERNAL_TRANSFER';
const GROUP_LABELS: Record<OpGroup, string> = {
    INCOME: 'Выручка', EXPENSE: 'Расходы', SALARY: 'Зарплата официальная', UNOFFICIAL_SALARY: 'Зарплата неофициальная',
    SELF_EMPLOYED: 'Самозанятые', TAX: 'Налоги (ЕНС, СФР)', OWNER_WITHDRAWAL: 'Вывод себе', DIVIDENDS: 'Дивиденды',
    NON_TAX: 'Займы и переводы', INTERNAL_TRANSFER: 'Переводы между ИП',
};

const groupOf = (t: Transaction, cp?: Counterparty): OpGroup => {
    if (t.kind === 'NON_TAX') return 'NON_TAX';
    if (t.kind === 'INTERNAL_TRANSFER') return 'INTERNAL_TRANSFER';
    if (t.type === TransactionType.INCOME) return 'INCOME';
    if (t.kind === 'SALARY' || t.kind === 'UNOFFICIAL_SALARY' || t.kind === 'TAX' || t.kind === 'OWNER_WITHDRAWAL' || t.kind === 'DIVIDENDS') return t.kind;
    if (cp?.type === CounterpartyType.INDIVIDUAL) return 'SELF_EMPLOYED';
    return 'EXPENSE';
};

export const Reports: React.FC = () => {
  const { state, showNotification } = useData();
  const [packing, setPacking] = useState(false);
  const [packInfo, setPackInfo] = useState<{ files: number; operations: number; missing: { document: string; reason: string }[] } | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [counterpartyId, setCounterpartyId] = useState('');
  const [group, setGroup] = useState<OpGroup | ''>('');
  const [method, setMethod] = useState<'' | 'bank' | 'cash'>('');
  const [orgFilter, setOrgFilter] = useState('');
  const [author, setAuthor] = useState('');

  const isAll = state.currentOrganizationId === ALL_ORGS;
  const orgOf = (t: Transaction) => state.organizations.find(o => o.id === t.organizationId);
  const getCounterparty = (id?: string | null) => state.counterparties.find(c => c.id === id);

  // Налоговым операциям — получатель ФНС или СФР, выводу себе — сам предприниматель
  const counterpartyName = (t: Transaction) => {
      if (t.kind === 'TAX') return t.taxAccount === 'SFR' ? 'СФР' : 'ФНС (ЕНС)';
      if (t.kind === 'OWNER_WITHDRAWAL') return 'Вывод себе';
      return getCounterparty(t.counterpartyId)?.name || 'Без контрагента';
  };
  const methodLabel = (t: Transaction) => {
      const acc = orgOf(t)?.accounts.find(a => a.id === t.bankAccountId);
      if (acc) return acc.kind === 'cash' ? (acc.name || 'Наличные') : accountLabel(acc);
      return t.paymentMethod === 'cash' ? 'Наличные' : 'Безнал';
  };
  const authorName = (t: Transaction) => state.users.find(u => u.id === t.userId)?.name || (t.userId ? 'Пользователь' : 'Система');
  const documentLabel = (t: Transaction) => t.type === TransactionType.INCOME || t.kind === 'TAX' ? '' : t.hasDocument === false ? 'нет' : 'есть';

  // Движение денег: только проведённые операции
  const rows = useMemo(() => state.transactions.filter(t => {
      if (t.status !== TransactionStatus.PAID) return false;
      if (!isAll && t.organizationId !== state.currentOrganizationId) return false;
      if (isAll && orgFilter && t.organizationId !== orgFilter) return false;
      if (author && (t.userId || 'system') !== author) return false;
      if (dateFrom && t.date < dateFrom) return false;
      if (dateTo && t.date > dateTo) return false;
      if (counterpartyId && t.counterpartyId !== counterpartyId) return false;
      if (group && groupOf(t, getCounterparty(t.counterpartyId)) !== group) return false;
      if (method && (t.paymentMethod || 'bank') !== method) return false;
      return true;
  }).sort((a, b) => b.date.localeCompare(a.date) || (b.createdAt || '').localeCompare(a.createdAt || '')),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [state.transactions, state.counterparties, state.currentOrganizationId, dateFrom, dateTo, counterpartyId, group, method, orgFilter, author, isAll]);

  // Переводы между ИП — не приход и не расход (в сводном режиме они взаимно гасятся)
  const money = rows.filter(t => t.kind !== 'INTERNAL_TRANSFER');
  const totalIncome = money.filter(t => t.type === TransactionType.INCOME).reduce((s, t) => s + t.amount, 0);
  const totalExpense = money.filter(t => t.type === TransactionType.EXPENSE).reduce((s, t) => s + t.amount, 0);

  // Выгрузка: «;», UTF-8 с BOM — открывается в Excel и загружается в 1С
  const exportCsv = () => {
      const esc = (v: string) => /[;"\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
      const amountCell = (n: number) => n ? n.toFixed(2).replace('.', ',') : '';
      const header = [...(isAll ? ['Организация'] : []), 'Дата', 'Контрагент', 'ИНН', 'Назначение платежа', 'Приход', 'Расход', 'Способ оплаты', 'Документ', 'Автор'];
      const lines = rows.map(t => {
          const cp = getCounterparty(t.counterpartyId);
          const income = t.type === TransactionType.INCOME;
          return [...(isAll ? [orgDisplayName(orgOf(t))] : []), t.date.split('-').reverse().join('.'), counterpartyName(t), cp?.inn || '',
                  t.description || t.category || '', amountCell(income ? t.amount : 0), amountCell(income ? 0 : t.amount), methodLabel(t),
                  documentLabel(t), authorName(t)].map(v => esc(String(v))).join(';');
      });
      const blob = new Blob(['﻿' + [header.join(';'), ...lines].join('\r\n')], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `operations_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  // Все документы по контрагенту за период — ZIP (договоры, приложения, счета, акты, СФ, ЭДО, чеки «Мой налог», реестр)
  const downloadDocs = async () => {
      setPacking(true); setPackInfo(null);
      try {
          const scope = isAll ? (orgFilter || undefined) : state.currentOrganizationId;
          const r = await api.counterpartyDocuments(counterpartyId, dateFrom || undefined, dateTo || undefined, scope);
          setPackInfo(r);
          window.open(api.fileUrl(r.fileName), '_blank');
      } catch (e: any) { showNotification(e?.message || 'Ошибка', 'error'); }
      finally { setPacking(false); }
  };

  const reset = () => { setDateFrom(''); setDateTo(''); setCounterpartyId(''); setGroup(''); setMethod(''); setOrgFilter(''); setAuthor(''); };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-slate-900">Отчёты</h1>
          <Link to="/import" className="text-sm text-blue-600 hover:underline">Импорт операций из Excel →</Link>
      </div>

      <Card className="bg-white">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 items-end">
              <Input label="С даты" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
              <Input label="По дату" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
              <Select label="Контрагент" value={counterpartyId} onChange={e => setCounterpartyId(e.target.value)} options={[
                  { value: '', label: 'Все контрагенты' },
                  ...state.counterparties.map(c => ({ value: c.id, label: c.name }))]} />
              <Select label="Тип операции" value={group} onChange={e => setGroup(e.target.value as OpGroup | '')} options={[
                  { value: '', label: 'Все типы' }, ...(Object.keys(GROUP_LABELS) as OpGroup[]).map(g => ({ value: g, label: GROUP_LABELS[g] }))]} />
              <Select label="Способ оплаты" value={method} onChange={e => setMethod(e.target.value as '' | 'bank' | 'cash')} options={[
                  { value: '', label: 'Любой' }, { value: 'bank', label: 'Безнал' }, { value: 'cash', label: 'Наличные' }]} />
              {isAll && <Select label="Организация" value={orgFilter} onChange={e => setOrgFilter(e.target.value)} options={[
                  { value: '', label: 'Все' }, ...state.organizations.map(o => ({ value: o.id, label: orgDisplayName(o) }))]} />}
              <Select label="Автор" value={author} onChange={e => setAuthor(e.target.value)} options={[
                  { value: '', label: 'Все' }, ...state.users.map(u => ({ value: u.id, label: u.name })), { value: 'system', label: 'Система' }]} />
              <div className="flex gap-2">
                  <Button variant="outline" onClick={reset} title="Сбросить"><RefreshCw size={18}/></Button>
                  <Button onClick={exportCsv}><Download className="mr-2 h-4 w-4"/> CSV</Button>
                  <Button variant="outline" onClick={downloadDocs} disabled={!counterpartyId || packing}
                      title={counterpartyId ? 'Все документы и чеки по контрагенту за период — ZIP' : 'Выберите контрагента'}>
                      {packing ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : <FileArchive className="mr-2 h-4 w-4"/>} Документы ZIP</Button>
              </div>
          </div>
          {packInfo && (
              <div className="mt-3 text-xs text-slate-600">
                  В архиве {packInfo.files} файлов и реестр ({packInfo.operations} операций).
                  {packInfo.missing.length > 0 && <details className="mt-1"><summary className="cursor-pointer text-amber-700">Нет в архиве: {packInfo.missing.length}</summary>
                      <ul className="list-disc pl-5 mt-1">{packInfo.missing.map((m, i) => <li key={i}>{m.document} — {m.reason}</li>)}</ul></details>}
              </div>)}
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-emerald-50 border-emerald-100">
              <h3 className="text-sm font-medium text-emerald-800">Итого приход</h3>
              <p className="text-2xl font-bold text-emerald-600 mt-1">{formatMoney(totalIncome)}</p>
          </Card>
          <Card className="bg-red-50 border-red-100">
              <h3 className="text-sm font-medium text-red-800">Итого расход</h3>
              <p className="text-2xl font-bold text-red-600 mt-1">{formatMoney(totalExpense)}</p>
          </Card>
          <Card className="bg-blue-50 border-blue-100">
              <h3 className="text-sm font-medium text-blue-800">Сальдо (приход − расход)</h3>
              <p className={`text-2xl font-bold mt-1 ${totalIncome - totalExpense >= 0 ? 'text-blue-600' : 'text-red-600'}`}>{formatMoney(totalIncome - totalExpense)}</p>
              <p className="text-[11px] text-blue-800/70 mt-1">Движение денег, а не прибыль: прибыль — на странице «Операции».</p>
          </Card>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 text-slate-700 border-b border-slate-200">
                      <tr>
                          <th className="px-4 py-3">Дата</th>
                          {isAll && <th className="px-4 py-3">Организация</th>}
                          <th className="px-4 py-3">Контрагент</th>
                          <th className="px-4 py-3">Тип</th>
                          <th className="px-4 py-3">Способ оплаты</th>
                          <th className="px-4 py-3">Документ</th>
                          <th className="px-4 py-3">НДС</th>
                          <th className="px-4 py-3 text-right">Сумма</th>
                      </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                      {rows.length === 0 ? (
                          <tr><td colSpan={isAll ? 8 : 7} className="px-4 py-8 text-center text-slate-400">Нет операций за выбранный период</td></tr>
                      ) : rows.map(t => {
                          const rate = t.hasVat ? (t.vatRate || 0) : 0;
                          const doc = documentLabel(t);
                          return (
                              <tr key={t.id} className="hover:bg-slate-50">
                                  <td className="px-4 py-3 text-slate-500 font-mono text-xs">{t.date}</td>
                                  {isAll && <td className="px-4 py-3 text-xs">{orgDisplayName(orgOf(t))}</td>}
                                  <td className="px-4 py-3 font-medium text-slate-900">{counterpartyName(t)}<div className="text-xs font-normal text-slate-500">{t.description}</div><div className="text-[10px] font-normal text-slate-400">{authorName(t)}</div></td>
                                  <td className="px-4 py-3 text-xs text-slate-600">{GROUP_LABELS[groupOf(t, getCounterparty(t.counterpartyId))]}</td>
                                  <td className="px-4 py-3 text-xs text-slate-600">{methodLabel(t)}</td>
                                  <td className={`px-4 py-3 text-xs ${doc === 'нет' ? 'text-amber-600 font-bold' : 'text-slate-400'}`}>{doc || '—'}</td>
                                  <td className="px-4 py-3 text-xs">{rate > 0 ? <span>{rate}% <span className="text-slate-400">{formatMoney(t.amount * rate / (100 + rate))}</span></span> : <span className="text-slate-300">—</span>}</td>
                                  <td className={`px-4 py-3 text-right font-bold whitespace-nowrap ${t.type === TransactionType.INCOME ? 'text-emerald-600' : 'text-red-600'}`}>
                                      {t.type === TransactionType.INCOME ? '+' : '−'}{formatMoney(t.amount)}
                                  </td>
                              </tr>
                          );
                      })}
                  </tbody>
              </table>
          </div>
          <div className="p-3 border-t border-slate-200 text-xs text-slate-500">Проведённые операции: {rows.length}</div>
      </div>
    </div>
  );
};
